import { TeethMaterialManager } from '../modules/TeethMaterialManager.js';

/**
 * Teeth Material Control Script
 * Provides easy-to-use functions for controlling teeth material properties
 */

let teethManager = null;

/**
 * Initialize the teeth material control system
 * Call this after your 3D model is loaded
 */
export function initTeethMaterialControl(model) {
    if (!model) {
        console.error("No model provided to initTeethMaterialControl");
        return false;
    }
    
    teethManager = new TeethMaterialManager();
    teethManager.initialize(model);
    
    // Make functions available globally for easy script access
    window.setTeethMetallicness = setTeethMetallicness;
    window.setTeethRoughness = setTeethRoughness;
    window.setTeethColor = setTeethColor;
    window.applyTeethPreset = applyTeethPreset;
    window.resetTeethMaterial = resetTeethMaterial;
    window.getTeethStatus = getTeethStatus;
    
    console.log("Teeth material control initialized and functions made available globally");
    console.log("Available functions:");
    console.log("- setTeethMetallicness(value) // 0.0 to 1.0");
    console.log("- setTeethRoughness(value) // 0.0 to 1.0");
    console.log("- setTeethColor(color) // hex color like 0xffffff");
    console.log("- applyTeethPreset(name) // 'realistic', 'ceramic', 'matte', 'glossy'");
    console.log("- resetTeethMaterial() // restore original properties");
    console.log("- getTeethStatus() // get current configuration");
    
    return true;
}

/**
 * Set the metallicness of teeth meshes
 * @param {number} value - Metallicness value from 0.0 (non-metallic) to 1.0 (fully metallic)
 */
export function setTeethMetallicness(value) {
    if (!teethManager) {
        console.error("Teeth material manager not initialized. Call initTeethMaterialControl() first.");
        return;
    }
    
    teethManager.setMetallicness(value);
}

/**
 * Set the roughness of teeth meshes
 * @param {number} value - Roughness value from 0.0 (mirror-like) to 1.0 (completely rough)
 */
export function setTeethRoughness(value) {
    if (!teethManager) {
        console.error("Teeth material manager not initialized. Call initTeethMaterialControl() first.");
        return;
    }
    
    teethManager.setRoughness(value);
}

/**
 * Set the color of teeth meshes
 * @param {number|string} color - Color as hex number (0xffffff) or string ('#ffffff')
 */
export function setTeethColor(color) {
    if (!teethManager) {
        console.error("Teeth material manager not initialized. Call initTeethMaterialControl() first.");
        return;
    }
    
    teethManager.setColor(color);
}

/**
 * Set the environment map intensity for teeth meshes
 * @param {number} value - Environment map intensity from 0.0 to 2.0
 */
export function setTeethEnvMapIntensity(value) {
    if (!teethManager) {
        console.error("Teeth material manager not initialized. Call initTeethMaterialControl() first.");
        return;
    }
    
    teethManager.setEnvMapIntensity(value);
}

/**
 * Set clearcoat properties for teeth meshes (gives an enamel-like effect)
 * @param {number} clearcoat - Clearcoat strength from 0.0 to 1.0
 * @param {number} clearcoatRoughness - Clearcoat roughness from 0.0 to 1.0 (optional)
 */
export function setTeethClearcoat(clearcoat, clearcoatRoughness = null) {
    if (!teethManager) {
        console.error("Teeth material manager not initialized. Call initTeethMaterialControl() first.");
        return;
    }
    
    teethManager.setClearcoat(clearcoat, clearcoatRoughness);
}

/**
 * Apply a material preset to teeth meshes
 * @param {string} presetName - Preset name: 'realistic', 'ceramic', 'matte', 'glossy'
 */
export function applyTeethPreset(presetName) {
    if (!teethManager) {
        console.error("Teeth material manager not initialized. Call initTeethMaterialControl() first.");
        return;
    }
    
    teethManager.applyPreset(presetName);
}

/**
 * Reset teeth materials to their original properties
 */
export function resetTeethMaterial() {
    if (!teethManager) {
        console.error("Teeth material manager not initialized. Call initTeethMaterialControl() first.");
        return;
    }
    
    teethManager.restoreOriginalMaterials();
}

/**
 * Get current teeth material status and configuration
 */
export function getTeethStatus() {
    if (!teethManager) {
        console.error("Teeth material manager not initialized. Call initTeethMaterialControl() first.");
        return null;
    }
    
    return teethManager.getStatus();
}

/**
 * Get available presets
 */
export function getTeethPresets() {
    if (!teethManager) {
        console.error("Teeth material manager not initialized. Call initTeethMaterialControl() first.");
        return [];
    }
    
    return teethManager.getAvailablePresets();
}

// Example usage functions that can be called directly from browser console
/**
 * Quick function to make teeth non-metallic (realistic)
 */
export function makeTeethRealistic() {
    setTeethMetallicness(0.0);
    setTeethRoughness(0.3);
    console.log("Applied realistic teeth settings (0% metallicness, 30% roughness)");
}

/**
 * Quick function to make teeth slightly metallic
 */
export function makeTeethSlightlyMetallic() {
    setTeethMetallicness(0.1);
    setTeethRoughness(0.2);
    console.log("Applied slightly metallic teeth settings (10% metallicness, 20% roughness)");
}

/**
 * Quick function to make teeth completely non-metallic
 */
export function makeTeethMatte() {
    setTeethMetallicness(0.0);
    setTeethRoughness(0.8);
    console.log("Applied matte teeth settings (0% metallicness, 80% roughness)");
}

// Make quick functions available globally
if (typeof window !== 'undefined') {
    window.makeTeethRealistic = makeTeethRealistic;
    window.makeTeethSlightlyMetallic = makeTeethSlightlyMetallic;
    window.makeTeethMatte = makeTeethMatte;
}